import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class SizePopupDialogue extends UcoastEl {
	static htmlSelector = 'size-popup-dialogue'
	static selectors = {
		inner: '[data-uc-size-popup-inner]',
		closeButton: '[data-uc-size-popup-close]',
	}
	static mobileBreakpoint = 750
	static viewportPadding = 20
	static popupGap = 8

	private hasShownOnce = false
	private transformedAncestor?: HTMLElement

	constructor() {
		super()

		this.addEventListener('keyup', (evt) => {
			if (evt.code === 'Escape') this.close()
		})
		this.addEventListener('click', (evt) => {
			if (evt.target === this) this.close()
		})

		const closeButtons = q.rl(SizePopupDialogue.selectors.closeButton, this)
		closeButtons.forEach((button) => {
			button.addEventListener('click', this.close.bind(this))
		})
	}

	show(anchorElement: HTMLElement) {
		if (!anchorElement) return

		const shouldShowOnlyOnce = this.dataset.ucShowOnce !== 'false'
		if (shouldShowOnlyOnce && this.hasShownOnce) return

		this.hasShownOnce = true

		const isMobile = window.innerWidth < SizePopupDialogue.mobileBreakpoint
		const isDrawerMode = this.dataset.ucSizePopupMode === 'drawer'
		const isStickyAtc = anchorElement.classList.contains('product-sticky-atc__variant-button')
		let slideDirection: 'left' | 'right' = 'left'

		this.prepareForMeasurement()

		if (isMobile || isDrawerMode) {
			this.applyMobilePosition()
			if (isDrawerMode) slideDirection = 'right'
		} else {
			const popupRect = this.getBoundingClientRect()
			const anchorRect = anchorElement.getBoundingClientRect()

			if (isStickyAtc) {
				slideDirection = this.positionStickyDesktop(anchorRect, popupRect)
			} else {
				slideDirection = this.positionInlineDesktop(anchorRect, popupRect)
			}
		}

		this.setAttribute('data-slide-direction', slideDirection)

		this.style.visibility = ''
		this.style.pointerEvents = ''
		if (isMobile || isDrawerMode) {
			this.transformedAncestor = this.closest('.product__info-wrapper') as HTMLElement | null || undefined
			this.transformedAncestor?.classList.add('size-popup-dialogue-parent-open')
			document.body.classList.add('overflow-hidden')
		}
		this.classList.add('active')

		window.TsDOM.trapFocus(this, q.rs('[role="dialog"]', this))
	}

	close() {
		this.classList.remove('active')
		this.transformedAncestor?.classList.remove('size-popup-dialogue-parent-open')
		this.transformedAncestor = undefined
		document.body.classList.remove('overflow-hidden')
		window.TsDOM.removeTrapFocus()
	}

	reset() {
		this.hasShownOnce = false
		this.close()
	}

	private prepareForMeasurement() {
		this.style.visibility = 'hidden'
		this.style.pointerEvents = 'none'
		this.style.transform = ''
	}

	private applyMobilePosition() {
		this.style.position = 'fixed'
		this.style.top = '0'
		this.style.left = '0'
		this.style.right = '0'
		this.style.bottom = '0'
		this.style.transform = ''
	}

	private applyDesktopPosition(top: number, left: number) {
		this.style.position = 'fixed'
		this.style.top = `${top}px`
		this.style.left = `${left}px`
		this.style.right = 'auto'
		this.style.bottom = 'auto'
	}

	private positionStickyDesktop(anchorRect: DOMRect, popupRect: DOMRect) {
		const viewportWidth = window.innerWidth
		const viewportHeight = window.innerHeight
		const popupWidth = popupRect.width
		const popupHeight = popupRect.height
		let slideDirection: 'left' | 'right' = 'left'

		let left = anchorRect.left - popupWidth - SizePopupDialogue.popupGap
		if (left < SizePopupDialogue.viewportPadding) {
			left = anchorRect.right + SizePopupDialogue.popupGap
			slideDirection = 'right'
		}

		left = this.clamp(
			left,
			SizePopupDialogue.viewportPadding,
			viewportWidth - popupWidth - SizePopupDialogue.viewportPadding
		)

		const top = this.clamp(
			anchorRect.top + (anchorRect.height - popupHeight) / 2,
			SizePopupDialogue.viewportPadding,
			viewportHeight - popupHeight - SizePopupDialogue.viewportPadding
		)

		this.applyDesktopPosition(top, left)

		return slideDirection
	}

	private positionInlineDesktop(anchorRect: DOMRect, popupRect: DOMRect) {
		const viewportWidth = window.innerWidth
		const viewportHeight = window.innerHeight
		const popupWidth = popupRect.width
		const popupHeight = popupRect.height
		let slideDirection: 'left' | 'right' = 'left'
		let top = anchorRect.bottom + SizePopupDialogue.popupGap

		if (top + popupHeight > viewportHeight - SizePopupDialogue.viewportPadding) {
			top = anchorRect.top - popupHeight - SizePopupDialogue.popupGap
		}

		top = this.clamp(
			top,
			SizePopupDialogue.viewportPadding,
			viewportHeight - popupHeight - SizePopupDialogue.viewportPadding
		)

		let left = this.clamp(
			anchorRect.left,
			SizePopupDialogue.viewportPadding,
			viewportWidth - popupWidth - SizePopupDialogue.viewportPadding
		)

		if (left > anchorRect.right) {
			slideDirection = 'right'
		}

		this.applyDesktopPosition(top, left)

		return slideDirection
	}

	private clamp(value: number, min: number, max: number) {
		if (max < min) return min

		return Math.min(Math.max(value, min), max)
	}

	/**
	 * Find the SizePopupDialogue instance for the MainProduct section
	 * containing the given element.
	 */
	static findForSection(element: HTMLElement): SizePopupDialogue | null {
		const sectionEl = element.closest('section[id^="MainProduct-"]')
		const sectionIdAttr = sectionEl?.getAttribute('id') || ''
		const sectionId = sectionIdAttr.replace('shopify-section-', '').replace('MainProduct-', '')
		if (!sectionId) return null

		return document.querySelector<SizePopupDialogue>(`#SizePopupDialogue-${sectionId}`)
	}
}
